import React from "react";

import useOnScreen from "./useOnScreen";

/**
 * Position and size of an element at the moment it entered the viewport.
 *
 * Offsets are absolute and measured from the top-left corner of the page
 * (not the viewport). The scroll position at the time of capture is
 * also recorded, so the relative position within the viewport
 * is still recoverable if needed via `offsetY - scrollY`.
 */
export type OnScreenGeometry = {
  offsetX: number;
  offsetY: number;
  width: number;
  height: number;
  scrollX: number;
  scrollY: number;
};

function buildGeometry(rect: DOMRectReadOnly): OnScreenGeometry {
  const scrollX = window.scrollX;
  const scrollY = window.scrollY;

  return {
    offsetX: Math.round(rect.left + scrollX),
    offsetY: Math.round(rect.top + scrollY),
    width: Math.round(rect.width),
    height: Math.round(rect.height),
    scrollX: Math.round(scrollX),
    scrollY: Math.round(scrollY),
  };
}

type OnScreenWithGeometry = {
  isOnScreen: boolean;
  geometry: React.MutableRefObject<OnScreenGeometry | null>;
};

/**
 * Tracks whether an element is in the viewport, and records where it was when
 * it got there.
 *
 * `geometry` is a ref, not state: it is already set when a render first sees
 * `isOnScreen` turn true, so callers can read it during that render.
 */
export default function useOnScreenWithGeometry(
  node?: Element | null,
  initialValue = false,
  options: IntersectionObserverInit = {},
): OnScreenWithGeometry {
  const geometry = React.useRef<OnScreenGeometry | null>(null);

  const isOnScreen = useOnScreen(node, initialValue, options, entry => {
    if (entry.isIntersecting) {
      geometry.current = buildGeometry(entry.boundingClientRect);
    }
  });

  return { isOnScreen, geometry };
}
